import React, { useState } from 'react';
import DashboardLayout from '../../components/includes/DashboardLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { settingsApi, Gallery, CreateGalleryData } from "../../api/settings";
import { useEffect } from 'react';
import {
  // Save,
  Settings as SettingsIcon,
  Bell,
  Shield,
  Database,
  Users,
  Download,
  Upload,
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Globe,
  FileEdit
} from "lucide-react";

const Settings: React.FC = () => {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    siteName: 'ISTMECH',
    siteDescription: 'Institut Supérieur de Technologie Médicale du Cap-Haïtien',
    contactEmail: 'info@istmech.net',
    contactPhone: '+509 3456-7890',
    address: 'Cap-Haïtien, Haïti',
    enableNotifications: true,
    enableEmailAlerts: true,
    enableSMSAlerts: false,
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: true,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requireStrongPassword: true
  });

  const [users, setUsers] = useState<any[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Add User State
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '+509 ',
    role: 'USER'
  });

  // View User State
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isViewUserOpen, setIsViewUserOpen] = useState(false);

  // Edit User State
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);

  const handleViewUser = (user: any) => {
    setSelectedUser(user);
    setIsViewUserOpen(true);
  };

  const handleEditUser = (user: any) => {
    const normalizeRole = (role: string) => {
      if (role === 'ADMIN') return 'SUPER_ADMIN';
      if (role === 'PROFESSOR') return 'TEACHER';
      return role;
    };

    setEditingUser({
      ...user,
      role: normalizeRole(user.role),
      isActive: user.isActive ?? true // Ensure boolean
    });
    setIsEditUserOpen(true);
  };

  // Delete User State
  const [isDeleteUserOpen, setIsDeleteUserOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);

  const handleDeleteUser = (user: any) => {
    setUserToDelete(user);
    setIsDeleteUserOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const { deleteUser } = await import('../../api/users');
      await deleteUser(userToDelete.id);
      toast({ title: "Succès", description: "Utilisateur supprimé avec succès." });
      setIsDeleteUserOpen(false);
      fetchUsers();
    } catch (error: any) {
      console.error("Failed to delete user:", error);
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de supprimer l'utilisateur." });
    }
  };

  const handleUpdateUser = async () => {
    // Basic Validation
    if (!editingUser.firstName || !editingUser.lastName || !editingUser.email || !editingUser.role) {
      toast({
        variant: "destructive",
        title: "Champs manquants",
        description: "Veuillez remplir tous les champs obligatoires."
      });
      return;
    }

    // Phone Validation (if changed)
    if (editingUser.phone && editingUser.phone !== '+509 ' && !editingUser.phone.startsWith('+509')) {
      toast({
        variant: "destructive",
        title: "Format invalide",
        description: "Le numéro de téléphone doit commencer par +509.",
      });
      return;
    }

    // Sanitize payload to send only allowed fields
    const payload = {
      firstName: editingUser.firstName,
      lastName: editingUser.lastName,
      email: editingUser.email,
      phone: editingUser.phone,
      role: editingUser.role,
      isActive: editingUser.isActive,
      // isVerified: editingUser.isVerified // Uncomment if we enable verification editing
    };

    try {
      const { updateUser } = await import('../../api/users');
      await updateUser(editingUser.id, payload);
      toast({ title: "Succès", description: "Utilisateur mis à jour avec succès !" });
      setIsEditUserOpen(false);
      fetchUsers();
    } catch (error: any) {
      console.error("Failed to update user:", error);
      const errorMessage = error.response?.data?.message || "Erreur lors de la mise à jour.";
      toast({ variant: "destructive", title: "Erreur", description: errorMessage });
    }
  };

  const fetchUsers = React.useCallback(async () => {
    try {
      // Import dynamically to avoid top-level await issues if any
      const { getUsers } = await import('../../api/users');
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger la liste des utilisateurs.",
      });
      setUsers([]);
    }
  }, [toast]);

  React.useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, fetchUsers]);

  const handleCreateUser = async () => {
    // Validation détaillée
    const missingFields = [];
    if (!newUser.firstName) missingFields.push('Prénom');
    if (!newUser.lastName) missingFields.push('Nom');
    if (!newUser.email) missingFields.push('Email');
    if (!newUser.phone) missingFields.push('Téléphone');
    if (!newUser.role) missingFields.push('Rôle');

    if (missingFields.length > 0) {
      toast({
        variant: "destructive",
        title: "Champs manquants",
        description: `Veuillez remplir les champs suivants : ${missingFields.join(', ')}.`,
      });
      return;
    }

    // Validation format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      toast({
        variant: "destructive",
        title: "Format invalide",
        description: "L'adresse email n'est pas valide.",
      });
      return;
    }

    const phoneRegex = /^\+509[0-9\s-]{8,}$/;

    // Check if only prefix is present
    if (newUser.phone.trim() === '+509') {
      toast({
        variant: "destructive",
        title: "Format invalide",
        description: "Veuillez entrer un numéro de téléphone après l'indicatif +509.",
      });
      return;
    }

    if (!newUser.phone.startsWith('+509')) {
      toast({
        variant: "destructive",
        title: "Format invalide",
        description: "Le numéro de téléphone doit commencer par +509.",
      });
      return;
    }

    if (!phoneRegex.test(newUser.phone)) {
      toast({
        variant: "destructive",
        title: "Format invalide",
        description: "Le numéro de téléphone n'est pas valide (doit commencer par +509 suivi des chiffres).",
      });
      return;
    }

    setIsSubmittingUser(true);
    try {
      const { createUser } = await import('../../api/users');
      await createUser(newUser);
      setIsAddUserOpen(false);
      setNewUser({ firstName: '', lastName: '', email: '', phone: '', role: 'USER' }); // Reset form
      toast({
        title: "Succès",
        description: "Utilisateur créé avec succès !",
      });
      fetchUsers(); // Refresh list
    } catch (error: any) {
      console.error("Failed to create user:", error);
      const errorMessage = error.response?.data?.message || "Erreur lors de la création de l'utilisateur.";
      toast({
        variant: "destructive",
        title: "Erreur",
        description: errorMessage,
      });
    } finally {
      setIsSubmittingUser(false);
    }
  };

  const filteredUsers = (Array.isArray(users) ? users : []).filter((user) => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return (
      fullName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      (user.email?.toLowerCase() || '').includes(userSearchTerm.toLowerCase()) ||
      (user.role?.toLowerCase() || '').includes(userSearchTerm.toLowerCase())
    );
  });

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  //  // Gallery State
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [isAddGalleryOpen, setIsAddGalleryOpen] = useState(false);
  const [isLoadingGallery, setIsLoadingGallery] = useState(false);
  const [newGallery, setNewGallery] = useState<CreateGalleryData>({
    title: '',
    imageUrl: '',
    category: 'general'
  });

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const data = await settingsApi.getAllGalleries();
      setGalleries(data);
    } catch (error) {
      console.error("Failed to fetch galleries", error);
    }
  };



  const handleDeleteGallery = async (id: string) => {
    try {
      await settingsApi.deleteGallery(id);
      toast({
        title: "Succès",
        description: "Image supprimée",
      });
      fetchGalleries();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'image",
        variant: "destructive",
      });
    }
  };

  // Gallery View/Edit State
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const [isViewGalleryOpen, setIsViewGalleryOpen] = useState(false);
  const [editingGalleryId, setEditingGalleryId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleEditGallery = (gallery: Gallery) => {
    setNewGallery({
      title: gallery.title,
      imageUrl: gallery.imageUrl,
      category: gallery.category,
      description: gallery.description || ''
    });
    setEditingGalleryId(gallery.id);
    setSelectedFile(null);
    setIsAddGalleryOpen(true);
  };

  const handleViewGallery = (gallery: Gallery) => {
    setSelectedGallery(gallery);
    setIsViewGalleryOpen(true);
  };

  const handleSaveGallery = async () => {
    if (!newGallery.title || (!newGallery.imageUrl && !selectedFile)) {
      toast({
        title: "Erreur",
        description: "Le titre et une image sont requis",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingGallery(true);
    try {
      let finalImageUrl = newGallery.imageUrl;

      // Upload file first if selected
      if (selectedFile) {
        finalImageUrl = await settingsApi.uploadGalleryImage(selectedFile);
      }

      const galleryData = { ...newGallery, imageUrl: finalImageUrl };

      if (editingGalleryId) {
        await settingsApi.updateGallery(editingGalleryId, galleryData);
        toast({ title: "Succès", description: "Image mise à jour" });
      } else {
        await settingsApi.createGallery(galleryData);
        toast({ title: "Succès", description: "Image ajoutée à la galerie" });
      }

      setNewGallery({ title: '', imageUrl: '', category: 'general', description: '' });
      setEditingGalleryId(null);
      setSelectedFile(null);
      setIsAddGalleryOpen(false);
      fetchGalleries();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Opération échouée",
        variant: "destructive",
      });
    } finally {
      setIsLoadingGallery(false);
    }
  };

  // const handleSave = () => {
  //   // Ici vous pourriez sauvegarder les paramètres
  //   alert('Paramètres sauvegardés avec succès !');
  // };

  const getInitials = (firstName: string, lastName: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '??';
  };

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "destructive" | "outline" => {
    const roleUpper = role?.toUpperCase() || '';
    if (['ADMIN', 'ADMINISTRATEUR', 'SUPER_ADMIN', 'DIRECTOR'].includes(roleUpper)) return 'destructive';
    if (['PROFESSOR', 'PROFESSEUR', 'TEACHER', 'COMPTABLE', 'SECRETARY'].includes(roleUpper)) return 'default';
    if (['STUDENT', 'ETUDIANT'].includes(roleUpper)) return 'secondary';
    return 'outline'; // USER
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Paramètres du Système
            </h1>
            <p className="text-muted-foreground mt-1">
              Configurez les préférences globales de l'application
            </p>
          </div>
          {/* <Button onClick={handleSave} className="w-full md:w-auto">
            <Save className="mr-2 h-4 w-4" /> Sauvegarder
          </Button> */}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Mobile Navigation */}
          <div className="md:hidden mb-4">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sélectionner une section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">
                  <div className="flex items-center gap-2">
                    <SettingsIcon className="h-4 w-4" />
                    <span>Général</span>
                  </div>
                </SelectItem>
                <SelectItem value="website">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    <span>Web Site</span>
                  </div>
                </SelectItem>
                <SelectItem value="notifications">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    <span>Notifications</span>
                  </div>
                </SelectItem>
                <SelectItem value="security">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    <span>Sécurité</span>
                  </div>
                </SelectItem>
                <SelectItem value="backup">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    <span>Sauvegarde</span>
                  </div>
                </SelectItem>
                <SelectItem value="users">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span>Utilisateurs</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <TabsList className="hidden md:grid w-full grid-cols-6 h-auto">
            <TabsTrigger value="general" className="gap-2 py-2">
              <SettingsIcon className="h-4 w-4" />
              <span className="hidden lg:inline">Général</span>
            </TabsTrigger>
            <TabsTrigger value="website" className="gap-2 py-2">
              <Globe className="h-4 w-4" />
              <span className="hidden lg:inline">Web Site</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2 py-2">
              <Bell className="h-4 w-4" />
              <span className="hidden lg:inline">Notifications</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2 py-2">
              <Shield className="h-4 w-4" />
              <span className="hidden lg:inline">Sécurité</span>
            </TabsTrigger>
            <TabsTrigger value="backup" className="gap-2 py-2">
              <Database className="h-4 w-4" />
              <span className="hidden lg:inline">Sauvegarde</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2 py-2">
              <Users className="h-4 w-4" />
              <span className="hidden lg:inline">Utilisateurs</span>
            </TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informations Générales</CardTitle>
                  <CardDescription>
                    Informations de base sur l'établissement et le site.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="siteName">Nom du site</Label>
                      <Input
                        id="siteName"
                        value={settings.siteName}
                        onChange={(e) => handleSettingChange('siteName', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactEmail">Email de contact</Label>
                      <Input
                        id="contactEmail"
                        type="email"
                        value={settings.contactEmail}
                        onChange={(e) => handleSettingChange('contactEmail', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactPhone">Téléphone</Label>
                      <Input
                        id="contactPhone"
                        type="tel"
                        value={settings.contactPhone}
                        onChange={(e) => handleSettingChange('contactPhone', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address">Adresse</Label>
                      <Input
                        id="address"
                        value={settings.address}
                        onChange={(e) => handleSettingChange('address', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="siteDescription">Description du site</Label>
                    <Textarea
                      id="siteDescription"
                      value={settings.siteDescription}
                      onChange={(e) => handleSettingChange('siteDescription', e.target.value)}
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Options du Site</CardTitle>
                  <CardDescription>
                    Contrôlez l'accès et la disponibilité de la plateforme.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex flex-row items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Mode Maintenance</Label>
                      <p className="text-sm text-muted-foreground">
                        Désactiver temporairement l'accès public au site.
                      </p>
                    </div>
                    <Switch
                      checked={settings.maintenanceMode}
                      onCheckedChange={(checked) => handleSettingChange('maintenanceMode', checked)}
                    />
                  </div>
                  <div className="flex flex-row items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Autoriser les inscriptions</Label>
                      <p className="text-sm text-muted-foreground">
                        Permettre aux nouveaux utilisateurs de créer un compte.
                      </p>
                    </div>
                    <Switch
                      checked={settings.allowRegistration}
                      onCheckedChange={(checked) => handleSettingChange('allowRegistration', checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Website Settings */}
          <TabsContent value="website" className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="col-span-1">
              <CardHeader>
                <CardTitle className="flex flex-row items-center justify-between gap-4">Galleries du Site
                  <Dialog open={isAddGalleryOpen} onOpenChange={(open) => {
                    setIsAddGalleryOpen(open);
                    if (!open) {
                      setNewGallery({ title: '', imageUrl: '', category: 'general', description: '' });
                      setEditingGalleryId(null);
                      setSelectedFile(null);
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Plus className="mr-2 h-4 w-4" />
                        Ajouter
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingGalleryId ? 'Modifier l\'image' : 'Ajouter une image'}</DialogTitle>
                        <DialogDescription>
                          {editingGalleryId ? 'Modifiez les détails de l\'image.' : 'Ajoutez une nouvelle image à la galerie du site.'}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="max-h-[80vh] overflow-y-auto px-1">
                        <div className="grid gap-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="title">Titre</Label>
                            <Input
                              id="title"
                              value={newGallery.title}
                              onChange={(e) => setNewGallery({ ...newGallery, title: e.target.value })}
                              placeholder="Titre de l'image"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="category">Catégorie</Label>
                            <Select
                              value={newGallery.category}
                              onValueChange={(val) => setNewGallery({ ...newGallery, category: val })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Choisir une catégorie" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="general">Général</SelectItem>
                                <SelectItem value="campus">Campus</SelectItem>
                                <SelectItem value="events">Événements</SelectItem>
                                <SelectItem value="students">Étudiants</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="imageUrl">Image</Label>
                            <div className="flex gap-2 items-center">
                              <Input
                                id="imageUrl"
                                value={selectedFile ? selectedFile.name : newGallery.imageUrl}
                                onChange={(e) => setNewGallery({ ...newGallery, imageUrl: e.target.value })}
                                placeholder="L'image s'affichera ici après sélection"
                                className="flex-1"
                                disabled
                              />
                              <div className="relative">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;

                                    setSelectedFile(file);
                                    // Create local preview URL
                                    const previewUrl = URL.createObjectURL(file);
                                    setNewGallery(prev => ({ ...prev, imageUrl: previewUrl }));
                                  }}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                  title="Choisir une image"
                                />
                                <Button type="button" variant="outline" size="icon" disabled={isLoadingGallery}>
                                  <Upload className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            {newGallery.imageUrl && (
                              <div className="mt-2 w-full max-h-[200px] overflow-hidden rounded-md border bg-muted flex items-center justify-center">
                                <img
                                  src={newGallery.imageUrl}
                                  alt="Aperçu"
                                  className="max-h-[200px] w-auto object-contain"
                                />
                              </div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="description">Description (Optionnel)</Label>
                            <Textarea
                              id="description"
                              value={newGallery.description || ''}
                              onChange={(e) => setNewGallery({ ...newGallery, description: e.target.value })}
                              placeholder="Description de l'image..."
                              rows={3}
                            />
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAddGalleryOpen(false)}>Annuler</Button>
                        <Button onClick={handleSaveGallery} disabled={isLoadingGallery}>
                          {isLoadingGallery ? 'Enregistrement...' : (editingGalleryId ? 'Enregistrer' : 'Ajouter')}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={isViewGalleryOpen} onOpenChange={setIsViewGalleryOpen}>
                    <DialogContent className="max-w-3xl">
                      <DialogHeader>
                        <DialogTitle>{selectedGallery?.title}</DialogTitle>
                      </DialogHeader>
                      {selectedGallery && (
                        <div className="grid gap-4">
                          <div className="aspect-video w-full overflow-hidden rounded-lg bg-muted">
                            <img
                              src={selectedGallery.imageUrl}
                              alt={selectedGallery.title}
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge>{selectedGallery.category}</Badge>
                              <span className="text-sm text-muted-foreground">
                                Créé le {new Date(selectedGallery.createdAt || '').toLocaleDateString()}
                              </span>
                            </div>
                            {selectedGallery.description && (
                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                {selectedGallery.description}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </CardTitle>
                <CardDescription>Ajoutez des images à votre site.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Image</TableHead>
                        <TableHead>Titre</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {galleries.length === 0 ? (
                        <TableRow>
                          <TableCell className="text-muted-foreground italic text-center" colSpan={4}>
                            Aucune image
                          </TableCell>
                        </TableRow>
                      ) : (
                        galleries.map((gallery) => (
                          <TableRow key={gallery.id}>
                            <TableCell>
                              <div className="h-10 w-16 bg-muted rounded overflow-hidden">
                                <img
                                  src={gallery.imageUrl}
                                  alt={gallery.title}
                                  className="h-full w-full object-cover"
                                  onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=No+Image' }}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{gallery.title}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">
                                {gallery.category}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                                    <span className="sr-only">Ouvrir menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => handleViewGallery(gallery)}
                                  >
                                    <Eye className="mr-2 h-4 w-4" /> Voir
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => handleEditGallery(gallery)}
                                  >
                                    <FileEdit className="mr-2 h-4 w-4" /> Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="cursor-pointer text-red-600 focus:text-red-600"
                                    onClick={() => handleDeleteGallery(gallery.id)}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-1">
              <CardHeader>
                <CardTitle>Configuration</CardTitle>
                <CardDescription>Paramètres généraux du site.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center h-40 text-muted-foreground border-2 border-dashed rounded-lg">
                  Contenu à venir
                </div>
              </CardContent>
            </Card>

          </TabsContent>

          {/* Notification Settings */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Préférences de Notification</CardTitle>
                <CardDescription>
                  Gérez comment et quand vous souhaitez être notifié.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-row items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label className="text-base">Notifications système</Label>
                    <p className="text-sm text-muted-foreground">
                      Recevoir les notifications importantes du tableau de bord.
                    </p>
                  </div>
                  <Switch
                    checked={settings.enableNotifications}
                    onCheckedChange={(checked) => handleSettingChange('enableNotifications', checked)}
                  />
                </div>
                <div className="flex flex-row items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label className="text-base">Alertes par email</Label>
                    <p className="text-sm text-muted-foreground">
                      Recevoir un résumé des activités par email.
                    </p>
                  </div>
                  <Switch
                    checked={settings.enableEmailAlerts}
                    onCheckedChange={(checked) => handleSettingChange('enableEmailAlerts', checked)}
                  />
                </div>
                <div className="flex flex-row items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label className="text-base">Alertes par SMS</Label>
                    <p className="text-sm text-muted-foreground">
                      Infos critiques par SMS (Frais supplémentaires).
                    </p>
                  </div>
                  <Switch
                    checked={settings.enableSMSAlerts}
                    onCheckedChange={(checked) => handleSettingChange('enableSMSAlerts', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Settings */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>Paramètres de Sécurité</CardTitle>
                <CardDescription>
                  Renforcez la sécurité de votre plateforme.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="sessionTimeout">Expiration de session</Label>
                    <Input
                      id="sessionTimeout"
                      type="number"
                      value={settings.sessionTimeout}
                      onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                    />
                    <p className="text-[0.8rem] text-muted-foreground">En minutes</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxLoginAttempts">Tentatives max</Label>
                    <Input
                      id="maxLoginAttempts"
                      type="number"
                      value={settings.maxLoginAttempts}
                      onChange={(e) => handleSettingChange('maxLoginAttempts', parseInt(e.target.value))}
                    />
                    <p className="text-[0.8rem] text-muted-foreground">Avant blocage</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="passwordMinLength">Longueur mot de passe</Label>
                    <Input
                      id="passwordMinLength"
                      type="number"
                      value={settings.passwordMinLength}
                      onChange={(e) => handleSettingChange('passwordMinLength', parseInt(e.target.value))}
                    />
                    <p className="text-[0.8rem] text-muted-foreground">Minimum caractères</p>
                  </div>
                </div>

                <div className="space-y-6 pt-4">
                  <div className="flex flex-row items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Vérification email obligatoire</Label>
                      <p className="text-sm text-muted-foreground">
                        Les utilisateurs doivent vérifier leur email avant de se connecter.
                      </p>
                    </div>
                    <Switch
                      checked={settings.requireEmailVerification}
                      onCheckedChange={(checked) => handleSettingChange('requireEmailVerification', checked)}
                    />
                  </div>
                  <div className="flex flex-row items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Mot de passe fort requis</Label>
                      <p className="text-sm text-muted-foreground">
                        Exiger des majuscules, chiffres et caractères spéciaux.
                      </p>
                    </div>
                    <Switch
                      checked={settings.requireStrongPassword}
                      onCheckedChange={(checked) => handleSettingChange('requireStrongPassword', checked)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup Settings */}
          <TabsContent value="backup">
            <div className="grid gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900">
                  <CardHeader>
                    <CardTitle className="text-blue-700 dark:text-blue-300 flex items-center gap-2">
                      <Download className="h-5 w-5" /> Sauvegarde
                    </CardTitle>
                    <CardDescription className="text-blue-600/80 dark:text-blue-400/80">
                      Créez une copie complète de votre base de données.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700">
                      Créer une sauvegarde
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-green-50/50 dark:bg-green-900/10 border-green-100 dark:border-green-900">
                  <CardHeader>
                    <CardTitle className="text-green-700 dark:text-green-300 flex items-center gap-2">
                      <Upload className="h-5 w-5" /> Restauration
                    </CardTitle>
                    <CardDescription className="text-green-600/80 dark:text-green-400/80">
                      Restaurer les données depuis un fichier local.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full border-green-200 text-green-700 hover:text-green-800 hover:bg-green-100 dark:border-green-800 dark:text-green-300 dark:hover:bg-green-900/20">
                      Choisir un fichier
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Historique des sauvegardes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { date: '2024-10-11 14:30', size: '2.4 MB', status: 'Complète' },
                      { date: '2024-10-10 14:30', size: '2.3 MB', status: 'Complète' },
                      { date: '2024-10-09 14:30', size: '2.2 MB', status: 'Complète' }
                    ].map((backup, index) => (
                      <div key={index} className="flex items-center justify-between p-4 rounded-lg border bg-card text-card-foreground shadow-sm">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-muted rounded-full">
                            <Database className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Sauvegarde automatique</p>
                            <p className="text-xs text-muted-foreground">{backup.date} • {backup.size}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm">Télécharger</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Settings */}
          <TabsContent value="users">
            <div className="grid gap-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Administrateurs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600">
                      {users.filter(u => ['ADMIN', 'SUPER_ADMIN', 'DIRECTOR'].includes(u.role)).length}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Accès complet au système</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Personnel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-green-600">
                      {users.filter(u => ['TEACHER', 'PROFESSOR', 'COMPTABLE', 'SECRETARY'].includes(u.role)).length}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Professeurs, Comptables, Secrétaires</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Étudiants</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600">
                      {users.filter(u => ['STUDENT'].includes(u.role)).length}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Consultation et devoirs</p>
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="relative w-full md:w-[300px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher un utilisateur..."
                    className="pl-8"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                  />
                </div>

                <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full md:w-auto">
                      <Plus className="mr-2 h-4 w-4" /> Ajouter Un Utilisateur
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Ajouter un nouvel utilisateur</DialogTitle>
                      <DialogDescription>
                        Remplissez les informations ci-dessous pour créer un nouveau compte.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">Prénom</Label>
                          <Input
                            id="firstName"
                            value={newUser.firstName}
                            onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Nom</Label>
                          <Input
                            id="lastName"
                            value={newUser.lastName}
                            onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Téléphone</Label>
                        <Input
                          id="phone"
                          value={newUser.phone}
                          onChange={(e) => {
                            let val = e.target.value;
                            // Si l'utilisateur essaie d'effacer le préfixe, on le remet
                            if (!val.startsWith('+509')) {
                              val = '+509 ';
                            }
                            setNewUser({ ...newUser, phone: val });
                          }}
                          placeholder="+509 3000-0000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="role">Rôle</Label>
                        <Select
                          value={newUser.role}
                          onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USER">Utilisateur (User)</SelectItem>
                            <SelectItem value="STUDENT">Étudiant (Student)</SelectItem>
                            <SelectItem value="TEACHER">Professeur (Teacher)</SelectItem>
                            <SelectItem value="SECRETARY">Secrétaire (Secretary)</SelectItem>
                            <SelectItem value="COMPTABLE">Comptable</SelectItem>
                            <SelectItem value="DIRECTOR">Directeur</SelectItem>
                            <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsAddUserOpen(false)} disabled={isSubmittingUser}>
                        Annuler
                      </Button>
                      <Button onClick={handleCreateUser} disabled={isSubmittingUser}>
                        {isSubmittingUser ? "Création..." : "Créer l'utilisateur"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* View User Dialog */}
                <Dialog open={isViewUserOpen} onOpenChange={setIsViewUserOpen}>
                  <DialogContent className="sm:max-w-[425px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Détails de l'utilisateur</DialogTitle>
                    </DialogHeader>
                    {selectedUser && (
                      <div className="grid gap-6 py-4">
                        <div className="flex flex-col items-center gap-4">
                          <Avatar className="h-24 w-24">
                            <AvatarImage src={selectedUser.avatar} alt={selectedUser.firstName} />
                            <AvatarFallback className="text-2xl">
                              {getInitials(selectedUser.firstName, selectedUser.lastName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-center">
                            <h2 className="text-xl font-bold">
                              {selectedUser.firstName} {selectedUser.lastName}
                            </h2>
                            <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                          </div>
                          <Badge variant={getRoleBadgeVariant(selectedUser.role)} className="px-3 py-1">
                            {selectedUser.role}
                          </Badge>
                        </div>

                        <div className="space-y-4 border-t pt-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Téléphone</p>
                              <p className="font-medium">{selectedUser.phone || 'Non renseigné'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Statut</p>
                              <div className="flex items-center gap-2">
                                <span className={`h-2 w-2 rounded-full ${selectedUser.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                                <span className="font-medium">{selectedUser.isActive ? 'Actif' : 'Inactif'}</span>
                              </div>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Vérification</p>
                              <div className="flex items-center gap-1">
                                {selectedUser.isVerified ? (
                                  <>
                                    <Shield className="h-3 w-3 text-blue-500" />
                                    <span className="font-medium text-blue-500">Vérifié</span>
                                  </>
                                ) : (
                                  <span className="font-medium text-gray-500">Non vérifié</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-muted-foreground">ID</p>
                              <p className="font-mono text-xs truncate" title={selectedUser.id}>{selectedUser.id}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Edit User Dialog */}
                <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
                  <DialogContent className="sm:max-w-[425px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Modifier l'utilisateur</DialogTitle>
                      <DialogDescription>
                        Modifiez les informations de l'utilisateur ci-dessous.
                      </DialogDescription>
                    </DialogHeader>
                    {editingUser && (
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="edit-firstName">Prénom</Label>
                            <Input
                              id="edit-firstName"
                              value={editingUser.firstName}
                              onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-lastName">Nom</Label>
                            <Input
                              id="edit-lastName"
                              value={editingUser.lastName}
                              onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-email">Email</Label>
                          <Input
                            id="edit-email"
                            type="email"
                            value={editingUser.email}
                            onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-phone">Téléphone</Label>
                          <Input
                            id="edit-phone"
                            value={editingUser.phone || '+509 '}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (!val.startsWith('+509')) val = '+509 ';
                              setEditingUser({ ...editingUser, phone: val });
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-role">Rôle</Label>
                          <Select
                            value={editingUser.role}
                            onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un rôle" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USER">Utilisateur (User)</SelectItem>
                              <SelectItem value="STUDENT">Étudiant (Student)</SelectItem>
                              <SelectItem value="TEACHER">Professeur (Teacher)</SelectItem>
                              <SelectItem value="SECRETARY">Secrétaire (Secretary)</SelectItem>
                              <SelectItem value="COMPTABLE">Comptable (Accountant)</SelectItem>
                              <SelectItem value="DIRECTOR">Directeur (Director)</SelectItem>
                              <SelectItem value="SUPER_ADMIN">Administrateur (Super Admin)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between space-x-2 pt-2">
                          <Label htmlFor="edit-active" className="flex flex-col space-y-1">
                            <span>Statut du compte</span>
                            <span className="font-normal text-xs text-muted-foreground">
                              {editingUser.isActive ? 'Le compte est actif' : 'Le compte est désactivé'}
                            </span>
                          </Label>
                          <Switch
                            id="edit-active"
                            checked={editingUser.isActive}
                            onCheckedChange={(checked) => setEditingUser({ ...editingUser, isActive: checked })}
                          />
                        </div>
                      </div>
                    )}
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsEditUserOpen(false)}>
                        Annuler
                      </Button>
                      <Button onClick={handleUpdateUser}>
                        Sauvegarder
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Delete User Confirmation Dialog */}
                <Dialog open={isDeleteUserOpen} onOpenChange={setIsDeleteUserOpen}>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Confirmer la suppression</DialogTitle>
                      <DialogDescription>
                        Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.
                      </DialogDescription>
                    </DialogHeader>
                    {userToDelete && (
                      <div className="py-4 flex items-center gap-4 bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-100 dark:border-red-900">
                        <div className="h-10 w-10 text-red-500 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
                          <Trash2 className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-red-700 dark:text-red-400">
                            {userToDelete.firstName} {userToDelete.lastName}
                          </p>
                          <p className="text-sm text-red-600/80 dark:text-red-400/70">
                            {userToDelete.email}
                          </p>
                        </div>
                      </div>
                    )}
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsDeleteUserOpen(false)}>
                        Annuler
                      </Button>
                      <Button variant="destructive" onClick={confirmDeleteUser}>
                        Supprimer
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardContent className="p-0">
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Utilisateur</TableHead>
                          <TableHead>Rôle</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Vérifié</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="h-24 text-center">
                              Aucun utilisateur trouvé.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredUsers.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell className="font-medium whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-9 w-9">
                                    <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                    <AvatarFallback>{getInitials(user.firstName, user.lastName)}</AvatarFallback>
                                  </Avatar>
                                  <div className="flex flex-col">
                                    <span className="font-semibold">{user.firstName} {user.lastName}</span>
                                    <span className="text-xs text-muted-foreground">{user.email}</span>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                <Badge variant={getRoleBadgeVariant(user.role)}>
                                  {user.role}
                                </Badge>
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                <div className={`flex items-center gap-2 ${user.isActive ? 'text-green-600' : 'text-gray-500'
                                  }`}>
                                  <div className={`h-2 w-2 rounded-full ${user.isActive ? 'bg-green-600' : 'bg-gray-400'
                                    }`} />
                                  <span className="text-sm font-medium">{user.isActive ? 'Actif' : 'Inactif'}</span>
                                </div>
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                {user.isVerified ? (
                                  <div className="flex items-center text-blue-600">
                                    <Shield className="h-4 w-4 mr-1" />
                                    <span className="text-sm">Vérifié</span>
                                  </div>
                                ) : (
                                  <span className="text-sm text-muted-foreground">Non vérifié</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right whitespace-nowrap">
                                {user.id === currentUser?.id ? (
                                  <Button variant="ghost" className="h-8 w-8 p-0" disabled>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" className="h-8 w-8 p-0">
                                        <span className="sr-only">Ouvrir menu</span>
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem className="cursor-pointer" onClick={() => handleViewUser(user)}>
                                        <Eye className="mr-2 h-4 w-4" /> Voir
                                      </DropdownMenuItem>
                                      <DropdownMenuItem className="cursor-pointer" onClick={() => handleEditUser(user)}>
                                        <Edit className="mr-2 h-4 w-4" /> Modifier
                                      </DropdownMenuItem>
                                      <DropdownMenuItem className="cursor-pointer text-red-600 focus:text-red-600" onClick={() => handleDeleteUser(user)}>
                                        <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile List View */}
                  <div className="md:hidden">
                    {filteredUsers.length === 0 ? (
                      <div className="p-8 text-center text-muted-foreground">
                        Aucun utilisateur trouvé.
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filteredUsers.map((user) => (
                          <div key={user.id} className="p-4 flex items-start justify-between bg-card">
                            <div className="flex items-start gap-4">
                              <Avatar className="h-10 w-10 mt-1">
                                <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                <AvatarFallback>{getInitials(user.firstName, user.lastName)}</AvatarFallback>
                              </Avatar>
                              <div className="space-y-1">
                                <div className="font-semibold">
                                  {user.firstName} {user.lastName}
                                </div>
                                <div className="text-sm text-muted-foreground break-all">
                                  {user.email}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <Badge variant={getRoleBadgeVariant(user.role)} className="px-2 py-0.5 text-xs">
                                    {user.role}
                                  </Badge>
                                  <div className={`flex items-center gap-1.5 text-xs font-medium ${user.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                                    <div className={`h-1.5 w-1.5 rounded-full ${user.isActive ? 'bg-green-600' : 'bg-gray-400'}`} />
                                    {user.isActive ? 'Actif' : 'Inactif'}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {user.id === currentUser?.id ? (
                              <Button variant="ghost" className="h-8 w-8 p-0" disabled>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" className="h-8 w-8 p-0">
                                    <span className="sr-only">Ouvrir menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="cursor-pointer" onClick={() => handleViewUser(user)}>
                                    <Eye className="mr-2 h-4 w-4" /> Voir
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="cursor-pointer" onClick={() => handleEditUser(user)}>
                                    <Edit className="mr-2 h-4 w-4" /> Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="cursor-pointer text-red-600 focus:text-red-600" onClick={() => handleDeleteUser(user)}>
                                    <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent >
        </Tabs >
      </div >
    </DashboardLayout >
  );
};

export default Settings;
